import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { useUserStore, useSubscriptionStore } from 'store';
import { getCsrfToken } from '../../utils';

const getTreadColor = (depth) => {
  if (depth == null) return 'text.secondary';
  const d = parseFloat(depth);
  if (isNaN(d)) return 'text.secondary';
  if (d < 3) return 'error.main';
  if (d < 4) return 'warning.main';
  return 'success.main';
};

function ConditionReportDialog({ t, open, onClose, card, onSent }) {
  const { currentOrganization } = useUserStore();
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();

  const hasEmail = !!(card?.customer_email);
  const hasPhone = !!(card?.customer_phone);

  // Check if SMS is available (not available during trial)
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  const [sendEmail, setSendEmail] = useState(hasEmail);
  const [sendSms, setSendSms] = useState(hasPhone && smsEnabled);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state when dialog opens with new card
  useEffect(() => {
    if (open) {
      setSendEmail(!!card?.customer_email);
      setSendSms(!!card?.customer_phone && smsEnabled);
      setSending(false);
      setResult(null);
      if (!subscriptionInfo) {
        fetchSubscriptionInfo();
      }
    }
  }, [open, card]);

  const handleSend = async () => {
    if (!card || (!sendEmail && !sendSms)) return;

    setSending(true);
    setResult(null);
    try {
      const response = await axios.post(
        `/api/tire-hotel-cards/${card.id}/send_condition_report/`,
        { send_email: sendEmail, send_sms: sendSms },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
            'X-Organization-Id': currentOrganization.organization_id,
          },
        }
      );
      setResult(response.data);
      if (onSent) onSent();
      // Auto-close dialog after short delay so user sees success briefly
      setTimeout(() => { onClose(); }, 1500);
    } catch (err) {
      setResult({ error: err.response?.data?.error || err.message });
    } finally {
      setSending(false);
    }
  };

  const hasSuccess = result && (result.email?.success || result.sms?.success);
  const hasError = result && (result.error || result.email?.error || result.sms?.error);

  if (!card) return null;

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="condition-report-dialog"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {t('tire_hotel.report_title')}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} disabled={sending} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Card Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('tire_hotel.report_vehicle')}
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {card.vehicle_registration_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {card.tire_brand} {card.tire_model} {card.tire_size_display}
          </Typography>
          {card.needs_replacement && (
            <Chip
              size="small"
              label={card.replacement_reason
                ? `${t('tire_hotel.needs_replacement')}: ${card.replacement_reason}`
                : t('tire_hotel.needs_replacement')}
              color="warning"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>

        {/* Tread Depths */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('tire_hotel.report_tread_depths')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, maxWidth: 300 }}>
            {['fl', 'fr', 'rl', 'rr'].map((pos) => {
              const value = card[`tread_depth_${pos}`];
              return (
                <Box key={pos} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 24 }}>
                    {t(`tire_hotel.tread_depth_${pos}`)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: getTreadColor(value) }}
                  >
                    {value != null ? `${value} mm` : '-'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {card.needs_replacement && (
            <Alert severity="warning" sx={{ mt: 1 }} variant="outlined">
              {t('tire_hotel.report_needs_replacement')}
            </Alert>
          )}
        </Box>

        {/* Contact & Channel Selection */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={!hasEmail || sending || !!result}
                data-testid="condition-report-send-email"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" color={hasEmail ? 'action' : 'disabled'} />
                <Box>
                  <Typography variant="body2">
                    {t('tire_hotel.report_send_email')}
                  </Typography>
                  {hasEmail ? (
                    <Typography variant="caption" color="text.secondary">
                      {card.customer_email}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="error">
                      {t('tire_hotel.report_no_email')}
                    </Typography>
                  )}
                </Box>
              </Box>
            }
          />

          <Tooltip
            title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''}
            placement="right"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  disabled={!hasPhone || !smsEnabled || sending || !!result}
                  data-testid="condition-report-send-sms"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmsIcon fontSize="small" color={hasPhone && smsEnabled ? 'action' : 'disabled'} />
                  <Box>
                    <Typography variant="body2" sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                      {t('tire_hotel.report_send_sms')}
                    </Typography>
                    {hasPhone ? (
                      <Typography variant="caption" color="text.secondary">
                        {card.customer_phone}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="error">
                        {t('tire_hotel.report_no_phone')}
                      </Typography>
                    )}
                  </Box>
                  {!smsEnabled && isTrial && (
                    <Tooltip title={t('SMS is not available during trial')}>
                      <InfoIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5 }} />
                    </Tooltip>
                  )}
                </Box>
              }
            />
          </Tooltip>
          {!smsEnabled && isTrial && (
            <Alert severity="info" sx={{ mt: 1, mb: 1, borderRadius: '8px' }}>
              {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
            </Alert>
          )}
        </Box>

        {/* Result */}
        {hasSuccess && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {t('tire_hotel.report_sent_success')}
          </Alert>
        )}
        {hasError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {t('tire_hotel.report_sent_error')}
            {result.error && (
              <Typography variant="caption" display="block">{result.error}</Typography>
            )}
            {result.email?.error && (
              <Typography variant="caption" display="block">Email: {result.email.error}</Typography>
            )}
            {result.sms?.error && (
              <Typography variant="caption" display="block">SMS: {result.sms.error}</Typography>
            )}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {result ? (
          <Button variant="contained" onClick={onClose}>
            {t('Close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={sending}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={(!sendEmail && !sendSms) || sending}
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              data-testid="condition-report-submit"
            >
              {sending ? t('tire_hotel.report_sending') : t('tire_hotel.report_send')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(ConditionReportDialog);
